"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import IndexBadges from "@/components/IndexBadges";

type ResistanceBreakoutRow = {
  symbol: string;
  name: string;
  sector: string;
  date: string;
  close: number;
  high: number;
  resistance: number;
  breakout_distance_pct: number;
  high_distance_pct: number;
  touches: number;
  violations: number;
  score: number;
  anchor1: string;
  anchor2: string;
  volume: number;
  avg_volume_20: number;
  rvol_20: number | null;
  indices?: string[];
};

type ResistanceBreakoutPayload = {
  ok?: boolean;
  rows?: ResistanceBreakoutRow[];
  count?: number;
  total_matches?: number;
  total_scanned?: number;
  cached?: boolean;
};

const formatNumber = (value: number, digits = 2) =>
  Number.isFinite(value)
    ? value.toLocaleString(undefined, { maximumFractionDigits: digits })
    : "—";

const formatPct = (value: number) => `${formatNumber(value, 2)}%`;

const formatRvol = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? `${formatNumber(value, 2)}x` : "—";

export default function TrendlineResistanceBreakoutScannerPage() {
  const [breakoutPct, setBreakoutPct] = useState("0.5");
  const [maxExtensionPct, setMaxExtensionPct] = useState("10");
  const [minRvol, setMinRvol] = useState("0");
  const [rows, setRows] = useState<ResistanceBreakoutRow[]>([]);
  const [payload, setPayload] = useState<ResistanceBreakoutPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    params.set("breakout_pct", breakoutPct);
    params.set("max_extension_pct", maxExtensionPct);
    params.set("min_rvol", minRvol);
    params.set("limit", "100");
    params.set("lookback", "200");
    return params.toString();
  }, [breakoutPct, maxExtensionPct, minRvol]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/scanner/trendline-resistance-breakout?${query}`);
        const json = await res.json();
        if (!res.ok || json.ok === false) {
          throw new Error(json.error ?? json.details ?? `HTTP ${res.status}`);
        }
        setPayload(json);
        setRows(Array.isArray(json.rows) ? json.rows : []);
      } catch (err) {
        setPayload(null);
        setRows([]);
        setError(err instanceof Error ? err.message : "Failed to load resistance breakout scan.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [query]);

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Trendline Resistance Breakout Scanner</h1>
          <p className="page-subtitle">
            Stocks closing above projected daily pivot-high resistance with volume context.
          </p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              gap: "1rem",
              marginBottom: "1.5rem",
              padding: "1rem",
              border: "1px solid var(--color-border)",
              borderRadius: 8,
              background: "rgba(15, 23, 42, 0.4)",
            }}
          >
            <div>
              <label htmlFor="breakout-pct" style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem" }}>
                Minimum close above resistance
              </label>
              <select
                id="breakout-pct"
                value={breakoutPct}
                onChange={(event) => setBreakoutPct(event.target.value)}
                style={{
                  width: "100%",
                  minHeight: 44,
                  borderRadius: 8,
                  border: "1px solid var(--color-border)",
                  background: "rgba(255,255,255,0.05)",
                  color: "var(--color-text)",
                  padding: "0.7rem",
                }}
              >
                <option value="0">Any close above</option>
                <option value="0.5">0.5% confirmed</option>
                <option value="1">1% strong</option>
                <option value="2">2% decisive</option>
              </select>
            </div>

            <div>
              <label htmlFor="max-extension" style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem" }}>
                Max extension
              </label>
              <select
                id="max-extension"
                value={maxExtensionPct}
                onChange={(event) => setMaxExtensionPct(event.target.value)}
                style={{
                  width: "100%",
                  minHeight: 44,
                  borderRadius: 8,
                  border: "1px solid var(--color-border)",
                  background: "rgba(255,255,255,0.05)",
                  color: "var(--color-text)",
                  padding: "0.7rem",
                }}
              >
                <option value="5">5% fresh only</option>
                <option value="10">10% standard</option>
                <option value="20">20% extended</option>
                <option value="100">No cap</option>
              </select>
            </div>

            <div>
              <label htmlFor="min-rvol" style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem" }}>
                Minimum RVOL
              </label>
              <select
                id="min-rvol"
                value={minRvol}
                onChange={(event) => setMinRvol(event.target.value)}
                style={{
                  width: "100%",
                  minHeight: 44,
                  borderRadius: 8,
                  border: "1px solid var(--color-border)",
                  background: "rgba(255,255,255,0.05)",
                  color: "var(--color-text)",
                  padding: "0.7rem",
                }}
              >
                <option value="0">No filter</option>
                <option value="1">1x+</option>
                <option value="1.5">1.5x+</option>
                <option value="2">2x+</option>
              </select>
            </div>

            <div>
              <span style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem" }}>
                Scan coverage
              </span>
              <div style={{ minHeight: 44, display: "flex", alignItems: "center", color: "var(--color-text-muted)" }}>
                {payload?.total_scanned ? `${payload.total_scanned} symbols scanned` : "Waiting for scan"}
              </div>
            </div>
          </div>

          <div className="results-table" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(138, 180, 248, 0.18)", borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ textAlign: "left", padding: "0.85rem" }}>Symbol</th>
                  <th style={{ textAlign: "left", padding: "0.85rem" }}>Company</th>
                  <th style={{ textAlign: "right", padding: "0.85rem" }}>Close</th>
                  <th style={{ textAlign: "right", padding: "0.85rem" }}>Resistance</th>
                  <th style={{ textAlign: "right", padding: "0.85rem" }}>Breakout</th>
                  <th style={{ textAlign: "right", padding: "0.85rem" }}>High vs Line</th>
                  <th style={{ textAlign: "right", padding: "0.85rem" }}>Touches</th>
                  <th style={{ textAlign: "left", padding: "0.85rem" }}>Anchors</th>
                  <th style={{ textAlign: "right", padding: "0.85rem" }}>Volume</th>
                  <th style={{ textAlign: "right", padding: "0.85rem" }}>20D Vol</th>
                  <th style={{ textAlign: "right", padding: "0.85rem" }}>RVOL</th>
                  <th style={{ textAlign: "right", padding: "0.85rem" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={12} style={{ padding: "1rem", color: "var(--color-text-muted)" }}>
                      Loading resistance breakout scan...
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td colSpan={12} style={{ padding: "1rem", color: "#fca5a5" }}>
                      {error}
                    </td>
                  </tr>
                )}
                {!loading && !error && rows.map((row) => (
                  <tr key={row.symbol} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "0.85rem", fontWeight: 700 }}>
                      <Link href={`/stock/${row.symbol}`} style={{ color: "inherit", textDecoration: "none" }}>
                        {row.symbol}
                      </Link>
                      <IndexBadges indices={row.indices ?? []} style={{ marginLeft: 5, verticalAlign: "middle" }} />
                    </td>
                    <td style={{ padding: "0.85rem", color: "var(--color-text-muted)", minWidth: 180 }}>
                      {row.name}
                    </td>
                    <td style={{ padding: "0.85rem", textAlign: "right", fontWeight: 600 }}>
                      {formatNumber(row.close)}
                    </td>
                    <td style={{ padding: "0.85rem", textAlign: "right", color: "#fca5a5", fontWeight: 600 }}>
                      {formatNumber(row.resistance)}
                    </td>
                    <td style={{ padding: "0.85rem", textAlign: "right", color: "var(--color-success)", fontVariantNumeric: "tabular-nums" }}>
                      {formatPct(row.breakout_distance_pct)}
                    </td>
                    <td style={{ padding: "0.85rem", textAlign: "right", color: "var(--color-text-muted)" }}>
                      {formatPct(row.high_distance_pct)}
                    </td>
                    <td style={{ padding: "0.85rem", textAlign: "right" }}>
                      {row.touches}
                    </td>
                    <td style={{ padding: "0.85rem", color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>
                      {row.anchor1} / {row.anchor2}
                    </td>
                    <td style={{ padding: "0.85rem", textAlign: "right", color: "var(--color-text-muted)" }}>
                      {formatNumber(row.volume, 0)}
                    </td>
                    <td style={{ padding: "0.85rem", textAlign: "right", color: "var(--color-text-muted)" }}>
                      {formatNumber(row.avg_volume_20, 0)}
                    </td>
                    <td
                      style={{
                        padding: "0.85rem",
                        textAlign: "right",
                        color: row.rvol_20 !== null && row.rvol_20 >= 1.5 ? "var(--color-success)" : "var(--color-text-muted)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatRvol(row.rvol_20)}
                    </td>
                    <td style={{ padding: "0.85rem", textAlign: "right", color: "var(--color-text-muted)" }}>
                      {row.date}
                    </td>
                  </tr>
                ))}
                {!loading && !error && rows.length === 0 && (
                  <tr>
                    <td colSpan={12} style={{ padding: "1rem", color: "var(--color-text-muted)" }}>
                      No resistance breakout matches for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
